import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  Save, 
  Trash2, 
  Euro, 
  MapPin, 
  Car, 
  Bike,
  Edit,
  X,
  ChevronDown,
  ChevronUp,
  Copy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLicenseClasses = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [licenseClasses, setLicenseClasses] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [newClassType, setNewClassType] = useState('');
  const [expandedClass, setExpandedClass] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchLocationClasses(selectedLocation);
    }
  }, [selectedLocation]);

  const fetchData = async () => {
    try {
      const [locationsRes, classTypesRes] = await Promise.all([
        axios.get(`${API_URL}/api/locations`),
        axios.get(`${API_URL}/api/license-class-types`)
      ]);
      
      setLocations(locationsRes.data);
      setClassTypes(classTypesRes.data);
      
      if (locationsRes.data.length > 0 && !selectedLocation) {
        setSelectedLocation(locationsRes.data[0].slug);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationClasses = async (locationSlug) => {
    try {
      const response = await axios.get(`${API_URL}/api/license-classes`);
      const locationClasses = response.data.filter(c => c.location_slug === locationSlug);
      setLicenseClasses(locationClasses);
    } catch (error) {
      console.error('Error fetching license classes:', error);
      toast.error('Fehler beim Laden der Führerscheinklassen');
    }
  };

  const handleAddClass = async () => {
    if (!newClassType || !selectedLocation) return;
    
    const classType = classTypes.find(ct => ct.type === newClassType);
    if (!classType) return;
    
    const locationName = locations.find(l => l.slug === selectedLocation)?.name || selectedLocation;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const newClass = {
        class_type: classType.type,
        class_name: classType.name,
        location_slug: selectedLocation,
        location_name: locationName,
        active: true,
        description: classType.description,
        min_age: classType.min_age,
        duration_weeks: 8,
        cost_components: classType.default_components.map(c => ({
          name: c.name,
          label: c.label,
          price: c.price,
          is_per_unit: c.is_per_unit || false,
          default_units: c.default_units || 1,
          min_units: c.min_units || 0,
          max_units: c.max_units || 100
        }))
      };
      
      await axios.post(`${API_URL}/api/license-classes`, newClass, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${classType.name} wurde hinzugefügt!`);
      setShowAddDialog(false);
      setNewClassType('');
      fetchLocationClasses(selectedLocation);
    } catch (error) {
      console.error('Error adding class:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen der Klasse');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateClass = async (classData) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API_URL}/api/license-classes/${classData.id}`, {
        class_name: classData.class_name,
        active: classData.active,
        description: classData.description,
        min_age: classData.min_age,
        duration_weeks: classData.duration_weeks,
        cost_components: classData.cost_components
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Änderungen gespeichert!');
      fetchLocationClasses(selectedLocation);
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClass = async (classId, className) => {
    if (!confirm(`Möchten Sie "${className}" wirklich löschen?`)) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/license-classes/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Klasse gelöscht!');
      fetchLocationClasses(selectedLocation);
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleComponentPriceChange = (classIndex, componentIndex, value) => {
    const updated = [...licenseClasses];
    updated[classIndex].cost_components[componentIndex].price = parseFloat(value) || 0;
    setLicenseClasses(updated);
  };

  const handleComponentUnitsChange = (classIndex, componentIndex, field, value) => {
    const updated = [...licenseClasses];
    updated[classIndex].cost_components[componentIndex][field] = parseInt(value) || 0;
    setLicenseClasses(updated);
  };

  const calculateTotal = (licenseClass) => {
    if (!licenseClass?.cost_components) return 0;
    return licenseClass.cost_components.reduce((sum, comp) => {
      if (comp.is_per_unit) {
        return sum + (comp.price * comp.default_units);
      }
      return sum + comp.price;
    }, 0);
  };

  const getClassIcon = (classType) => {
    if (['A', 'A1', 'A2', 'AM'].includes(classType)) {
      return <Bike className="w-5 h-5" />;
    }
    return <Car className="w-5 h-5" />;
  };

  // Get classes that are not yet added to this location
  const availableClassTypes = classTypes.filter(ct => 
    !licenseClasses.some(lc => lc.class_type === ct.type)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-license-classes">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Führerscheinklassen</h1>
          <p className="text-slate-600">Verwalten Sie die Führerscheinklassen und Preise pro Standort</p>
        </div>
      </div>

      {/* Location Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Standort auswählen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>Standort</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger data-testid="location-select">
                  <SelectValue placeholder="Standort wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(location => (
                    <SelectItem key={location.slug} value={location.slug}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={() => setShowAddDialog(true)}
              disabled={availableClassTypes.length === 0}
              data-testid="add-class-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Klasse hinzufügen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* License Classes List */}
      <div className="space-y-4">
        {licenseClasses.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Keine Klassen vorhanden</h3>
              <p className="text-slate-500 mb-4">
                Für diesen Standort wurden noch keine Führerscheinklassen angelegt.
              </p>
              <Button onClick={() => setShowAddDialog(true)} disabled={availableClassTypes.length === 0}>
                <Plus className="w-4 h-4 mr-2" />
                Erste Klasse hinzufügen
              </Button>
            </CardContent>
          </Card>
        ) : (
          licenseClasses.map((licenseClass, classIndex) => (
            <Card key={licenseClass.id} className="overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedClass(expandedClass === licenseClass.id ? null : licenseClass.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      licenseClass.active ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'
                    }`}>
                      {getClassIcon(licenseClass.class_type)}
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {licenseClass.class_name}
                        {!licenseClass.active && (
                          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                            Inaktiv
                          </span>
                        )}
                      </CardTitle>
                      <CardDescription>{licenseClass.description}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Geschätzter Gesamtpreis</p>
                      <p className="text-xl font-bold text-slate-900">
                        {calculateTotal(licenseClass).toLocaleString('de-DE')} €
                      </p>
                    </div>
                    {expandedClass === licenseClass.id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
              </CardHeader>
              
              {expandedClass === licenseClass.id && (
                <CardContent className="border-t bg-slate-50">
                  <div className="space-y-6 pt-4">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                        <span className="text-sm text-slate-600">Status</span>
                        <Switch
                          checked={licenseClass.active}
                          onCheckedChange={(checked) => {
                            const updated = [...licenseClasses];
                            updated[classIndex].active = checked;
                            setLicenseClasses(updated);
                          }}
                        />
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <Label className="text-xs text-slate-500">Mindestalter</Label>
                        <Input
                          type="number"
                          value={licenseClass.min_age || ''}
                          onChange={(e) => {
                            const updated = [...licenseClasses];
                            updated[classIndex].min_age = parseInt(e.target.value) || null;
                            setLicenseClasses(updated);
                          }}
                          className="mt-1"
                        />
                      </div>
                      <div className="p-3 bg-white rounded-lg border">
                        <Label className="text-xs text-slate-500">Dauer (Wochen)</Label>
                        <Input
                          type="number"
                          value={licenseClass.duration_weeks || ''}
                          onChange={(e) => {
                            const updated = [...licenseClasses];
                            updated[classIndex].duration_weeks = parseInt(e.target.value) || null;
                            setLicenseClasses(updated);
                          }}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* Cost Components */}
                    <div>
                      <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                        <Euro className="w-4 h-4" />
                        Kostenkomponenten
                      </h4>
                      <div className="space-y-2">
                        {licenseClass.cost_components?.map((component, compIndex) => (
                          <div 
                            key={component.name} 
                            className="flex items-center gap-4 p-3 bg-white rounded-lg border"
                          >
                            <div className="flex-1">
                              <Label className="text-sm font-medium">{component.label}</Label>
                              {component.is_per_unit && (
                                <span className="text-xs text-slate-500 ml-2">(pro Einheit)</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-24">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={component.price}
                                  onChange={(e) => handleComponentPriceChange(classIndex, compIndex, e.target.value)}
                                  className="text-right"
                                />
                              </div>
                              <span className="text-slate-500">€</span>
                              {component.is_per_unit && (
                                <>
                                  <span className="text-slate-400 mx-1">×</span>
                                  <div className="w-16">
                                    <Input
                                      type="number"
                                      value={component.default_units}
                                      onChange={(e) => handleComponentUnitsChange(classIndex, compIndex, 'default_units', e.target.value)}
                                      className="text-center"
                                      title="Standard-Anzahl"
                                    />
                                  </div>
                                  <span className="text-slate-500">=</span>
                                  <span className="font-medium w-20 text-right">
                                    {(component.price * component.default_units).toLocaleString('de-DE')} €
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-between pt-4 border-t">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteClass(licenseClass.id, licenseClass.class_name)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Löschen
                      </Button>
                      <Button
                        onClick={() => handleUpdateClass(licenseClass)}
                        disabled={saving}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? 'Speichern...' : 'Änderungen speichern'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Add Class Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Führerscheinklasse hinzufügen</DialogTitle>
            <DialogDescription>
              Wählen Sie eine Klasse für den Standort "{locations.find(l => l.slug === selectedLocation)?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Führerscheinklasse</Label>
              <Select value={newClassType} onValueChange={setNewClassType}>
                <SelectTrigger data-testid="class-type-select">
                  <SelectValue placeholder="Klasse wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {availableClassTypes.map(ct => (
                    <SelectItem key={ct.type} value={ct.type}>
                      <div className="flex items-center gap-2">
                        {getClassIcon(ct.type)}
                        <div>
                          <span className="font-medium">{ct.name}</span>
                          <span className="text-slate-500 ml-2 text-sm">- ab {ct.min_age} Jahre</span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {newClassType && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  {classTypes.find(ct => ct.type === newClassType)?.description}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Die Standardpreise können nach dem Hinzufügen angepasst werden.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleAddClass} disabled={!newClassType || saving}>
              {saving ? 'Hinzufügen...' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLicenseClasses;
